import { Outlet, Link, useLocation, NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/how-it-works', label: 'The Product', children: [
    { to: '/how-it-works#challenge',   label: 'How It Works' },
    { to: '/how-it-works#scaling',     label: 'Scaling Plan' },
    { to: '/how-it-works#rewards',     label: 'Rewards' },
    { to: '/how-it-works#instruments', label: 'Instruments' },
  ]},
  { to: '/about-us', label: 'About Us' },
  { to: '/blog', label: 'Blog' },
  { to: '/contact-us', label: 'Contact Us' },
];

export default function MarketingLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Inject the original Tailwind CSS once — it powers the design tokens
  // used by the cloned HTML.
  useEffect(() => {
    if (!document.querySelector('link[data-cloned-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/cloned/original.css';
      link.setAttribute('data-cloned-css', '');
      document.head.appendChild(link);
    }
    // Re-inject Inter font + Cousine font (the original CSS references these)
    if (!document.querySelector('link[data-fonts-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Cousine:wght@400;700&display=swap';
      link.setAttribute('data-fonts-css', '');
      document.head.appendChild(link);
    }
    // Wire up FAQ accordions + carousels in the cloned HTML
    if (!document.querySelector('script[data-cloned-interactions]')) {
      const s = document.createElement('script');
      s.src = '/cloned-interactions.js';
      s.setAttribute('data-cloned-interactions', '');
      document.body.appendChild(s);
    }
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#090c16] text-white">
      {/* React-rendered header — the cloned pages don't render their own header */}
      <header className="fixed inset-x-0 top-0 z-50">
        <div className="bg-[#080c16]/80 backdrop-blur border-b border-[#1f2640]/40">
          <div className="mx-auto w-full max-w-[1280px] px-6 flex h-[72px] items-center justify-between gap-4">
            <Link to="/" aria-label="Funder Pro" className="flex shrink-0 items-center">
              <img src="/cloned/wp-content/uploads/2024/10/Logo.svg" alt="Funder Pro" className="h-9 w-auto md:h-10" />
            </Link>

            <nav className="hidden lg:block">
              <ul className="flex items-center gap-1 rounded-[10px] bg-[#7e9aff]/[0.06] p-1">
                {NAV.map(item => (
                  <li key={item.to} className="group relative">
                    {item.children ? (
                      <>
                        <button className="flex items-center gap-1.5 rounded-[10px] px-4 py-2.5 text-base font-semibold text-white transition-colors hover:bg-white/5">
                          {item.label}
                          <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className="opacity-70 transition-transform group-hover:rotate-180">
                            <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                        </button>
                        <div className="invisible absolute left-0 top-full z-10 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                          <ul className="min-w-[240px] rounded-2xl border border-[#1f2640]/70 bg-[#080c16]/95 p-2 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)] backdrop-blur-md">
                            {item.children.map(c => (
                              <li key={c.to}>
                                <Link to={c.to} className="block rounded-[10px] px-4 py-3 text-base font-medium text-white/85 hover:bg-white/5 hover:text-white">
                                  {c.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </>
                    ) : (
                      <NavLink to={item.to} end={item.to === '/'}
                        className={({ isActive }) => `flex items-center gap-1.5 rounded-[10px] px-4 py-2.5 text-base font-semibold text-white transition-colors hover:bg-white/5 ${isActive ? 'bg-white/5' : ''}`}>
                        {item.label}
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </nav>

            <div className="hidden items-center gap-3 lg:flex">
              <Link to="/register" className="rounded-2xl border-2 border-[#345ffb] bg-[#080c16] px-6 py-[13px] text-base font-bold text-white transition-colors hover:bg-[#345ffb]/15" data-testid="nav-signin">
                Sign up / Log in
              </Link>
            </div>

            <button aria-label="Open menu" onClick={() => setOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#1f2640] text-white lg:hidden">
              <div className="space-y-[5px]">
                <span className="block h-0.5 w-5 bg-white" />
                <span className="block h-0.5 w-5 bg-white" />
                <span className="block h-0.5 w-5 bg-white" />
              </div>
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[60] bg-[#080c16]/95 backdrop-blur lg:hidden" onClick={() => setOpen(false)}>
          <div className="mx-auto w-full max-w-[1280px] px-6 pt-24" onClick={e => e.stopPropagation()}>
            <ul className="space-y-2">
              {NAV.map(item => (
                <li key={item.to}>
                  <Link to={item.to} onClick={() => setOpen(false)} className="block rounded-2xl border border-[#1f2640]/60 bg-[#0b101d] p-4 text-lg font-semibold">
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/register" onClick={() => setOpen(false)} className="rounded-2xl bg-[#345ffb] px-6 py-[13px] text-base font-bold text-white inline-flex" data-testid="nav-signin-mobile">
                  Sign up / Log in
                </Link>
              </li>
            </ul>
          </div>
        </div>
      )}

      <main key={pathname} className="flex-1 pt-[72px]">
        <Outlet />
      </main>

      <SiteFooter />
    </div>
  );
}

function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#080c16]">
      <div className="mx-auto w-full max-w-[1280px] px-6">
        <div className="rounded-t-[28px] border-x border-t border-[#1f2640]/50 pb-10 pt-12">
          <div className="grid gap-12 px-6 sm:px-10 lg:grid-cols-[1.3fr_2fr]">
            <div>
              <img src="/cloned/wp-content/uploads/2024/10/Logo.svg" alt="Funder Pro" className="h-8 w-auto" />
              <div className="mt-6 flex items-center gap-3 text-white/85">
                <SocialIcon kind="instagram" href="https://www.instagram.com/funderprofutures/" />
                <SocialIcon kind="discord"   href="https://discord.gg/eqFEg3QpcE" />
                <SocialIcon kind="telegram"  href="https://t.me/Funderprofutures" />
                <SocialIcon kind="twitter"   href="https://x.com/FPFutures" />
              </div>
              <p className="mt-6 max-w-[34ch] text-sm font-medium leading-6 text-[#9cabd6]">
                Cali House, Level 3, Vjal ir-Riħan, San Gwann, SGN 9020, Malta
              </p>
            </div>
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
              <FooterCol title="KEY LINKS" items={[
                ['How It Works', '/how-it-works'],
                ['About Us',      '/about-us'],
                ['Blog',          '/blog'],
              ]} />
              <FooterCol title="SUPPORT" items={[
                ['Contact Us',    '/contact-us'],
                ['FAQs',          '/contact-us'],
                ['Sitemap',       '/sitemap'],
              ]} />
              <FooterCol title="LEGAL" items={[
                ['Terms & Conditions', '/terms-conditions'],
                ['Privacy Notice',     '/privacy-notice'],
                ['Cookies Notice',     '/cookie-notice'],
                ['Risk Disclosure',    '/risk-disclosure'],
              ]} />
            </div>
          </div>
          <div className="mt-12 space-y-4 border-t border-[#1f2640]/40 px-6 pt-8 sm:px-10">
            <p className="text-xs leading-5 text-[#a9bbff]/80">
              FunderPro Ltd. is a company registered under the laws of the Republic of Malta with registration number C 104558 and registered address at Cali House, Level 3, Vjal ir-Riħan, San Gwann, SGN 9020, Malta, ("the Company"). The Company does not provide any of the investment services listed in the First Schedule of the Investment Services Act, Chapter 370 of the laws of Malta, is not a broker, and does not accept deposits.
            </p>
            <p className="text-xs leading-5 text-[#a9bbff]/80">
              Only 10.14% of traders who purchase a challenge successfully pass to the funded phase. Past performance is not an accurate indicator of future results.
            </p>
            <p className="pt-2 text-xs font-medium text-[#a9bbff]">© {year} FunderPro Ltd. All Rights Reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }: { title: string; items: [string, string][] }) {
  return (
    <div>
      <h5 className="mb-5 text-base font-semibold text-[#9cabd6]">{title}</h5>
      <ul className="space-y-3.5">
        {items.map(([label, href]) => (
          <li key={label}>
            <Link to={href} className="text-base font-medium text-white/85 hover:text-white">{label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialIcon({ kind, href }: { kind: 'instagram' | 'discord' | 'telegram' | 'twitter'; href: string }) {
  const paths: Record<string, JSX.Element> = {
    instagram: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" />
      </svg>
    ),
    discord: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M19.5 5.5A16 16 0 0015.5 4l-.2.4a12 12 0 013.5 1.8 11 11 0 00-9.6 0A12 12 0 0112.7 4.4L12.5 4A16 16 0 008.5 5.5C5.6 9.8 4.8 14 5.2 18a16 16 0 004.9 2.5l.6-1a10 10 0 01-1.7-.8l.4-.3a11.5 11.5 0 009.8 0l.4.3c-.5.3-1 .6-1.7.8l.6 1A16 16 0 0023.8 18c.5-4.6-.8-8.8-4.3-12.5zM9.7 15.5c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.6.8 1.6 1.8-.7 1.8-1.6 1.8zm4.6 0c-.9 0-1.6-.8-1.6-1.8s.7-1.8 1.6-1.8 1.6.8 1.6 1.8-.7 1.8-1.6 1.8z" />
      </svg>
    ),
    telegram: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M21.5 4.3L2.9 11.4c-1.1.4-1.1 1.1-.2 1.4l4.7 1.5 1.8 5.6c.2.6.4.6.8.4l2.6-1.9 4.6 3.4c.8.5 1.4.2 1.6-.8L22.9 5.4c.3-1.2-.4-1.7-1.4-1.1zM8.3 14.2l9.2-5.8c.4-.3.8-.1.5.2l-7.4 6.7-.3 3z" />
      </svg>
    ),
    twitter: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.2 3h3.3l-7.2 8.3L23 21h-6.6l-5.2-6.8L5.3 21H2l7.7-8.8L1.5 3h6.8l4.7 6.2L18.2 3z" />
      </svg>
    ),
  };
  return (
    <a href={href} aria-label={kind} className="flex h-6 w-6 items-center justify-center hover:text-white">{paths[kind]}</a>
  );
}
